/**
 * Config cards for the question editor.
 *
 *  · QuestionCommonRules  — Required + Hidden. Shown for EVERY value type
 *    (operator, 2026-07-30), so these two never live inside a type-specific card.
 *  · QuestionFileRules    — accepted formats, multiple files, size ceiling.
 *  · QuestionStepperRules — label/helper/min/max/default, and several named rows.
 *  · QuestionYesNoRules   — starting state + read-only for the toggle type.
 */
import { type CSSProperties, type ReactNode } from "react";
import { alpha, Btn, Card, Dropdown, Icon, IconBtn, T, Toggle } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import {
  Q_FILE_FORMATS,
  Q_FILE_SIZES,
  fileSize,
  newStepperRow,
  type QFileConfig,
  type QFileFormatId,
  type QFileSizeId,
  type QInfoConfig,
  type QInfoToneId,
  Q_INFO_TONES,
  type QSelectConfig,
  type QStepperConfig,
  type QStepperRow,
  type QYesNoConfig,
  type QYesNoStart,
} from "./question-model";

const inp = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});
const lbl: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 };
const hint: CSSProperties = { fontSize: 11.5, color: T.soft, lineHeight: 1.45, marginTop: 5, textWrap: "pretty" };
const cardTitle: CSSProperties = { fontSize: 13, fontWeight: 700, color: T.n1 };

function Row({ on, onToggle, title, sub }: { on: boolean; onToggle: () => void; title: string; sub: string }) {
  return (
    <label onClick={onToggle} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "2px 0" }}>
      <span style={{ marginTop: 1, flexShrink: 0 }}><Toggle on={on} /></span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, color: T.n1, fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45, textWrap: "pretty" }}>{sub}</span>
      </span>
    </label>
  );
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: 10, background: alpha(T.orange1, 10), border: `1px solid ${alpha(T.orange1, 28)}`, borderRadius: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
      <span style={{ color: T.orange1, flexShrink: 0, marginTop: 1, display: "inline-flex" }}><Icon.warn size={13} /></span>
      <span style={{ textWrap: "pretty" }}>{children}</span>
    </div>
  );
}

/* ── Required + Hidden — every value type ─────────────────────────────────── */
export function QuestionCommonRules({
  required, hidden, onRequired, onHidden,
}: {
  required: boolean;
  hidden: boolean;
  onRequired: (next: boolean) => void;
  onHidden: (next: boolean) => void;
}) {
  const t = useT();
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.common.behaviour", "Behaviour")}</div>
      <Row
        on={required}
        onToggle={() => onRequired(!required)}
        title={t("questions.textRules.required", "Required")}
        sub={t("questions.textRules.requiredSub", "The field must be filled before the booking can be submitted. Events can override this.")}
      />
      <Row
        on={hidden}
        onToggle={() => onHidden(!hidden)}
        title={t("questions.textRules.hidden", "Hidden in booking flow")}
        sub={t("questions.textRules.hiddenSub", "Not shown to the customer, but still stored and submitted — for prefilled or API-supplied values.")}
      />
      {hidden && required ? (
        <Warn>{t("questions.textRules.hiddenRequiredWarn", "A hidden field that is also required will block submission unless a value is always supplied (prefill or API). Make sure one is.")}</Warn>
      ) : null}
    </Card>
  );
}

/* ── Yes / no switch ──────────────────────────────────────────────────────── */
export function QuestionYesNoRules({ cfg, required, onChange }: { cfg: QYesNoConfig; required: boolean; onChange: (next: QYesNoConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QYesNoConfig>) => onChange({ ...cfg, ...patch });
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.yesno.switch", "Toggle")}</div>
      <div>
        <div style={lbl}>{t("questions.yesno.startsAs", "Starts as")}</div>
        <Dropdown
          options={[
            { value: "unset", label: t("questions.yesno.startUnset", "No answer yet"), supportingText: t("questions.yesno.startUnsetHint", "Neutral until the customer touches it") },
            { value: "no", label: t("common.no", "No"), supportingText: t("questions.yesno.startNoHint", "Off, and counts as answered") },
            { value: "yes", label: t("common.yes", "Yes"), supportingText: t("questions.yesno.startYesHint", "On, and counts as answered") },
          ]}
          value={cfg.startsAs}
          onChange={(v) => set({ startsAs: v as QYesNoStart })}
          aria-label={t("questions.yesno.startsAs", "Starts as")}
          testid="qyesno-starts-as"
          style={{ gap: 0, width: "100%" }}
        />
        <div style={hint}>{t("questions.yesno.startsAsHint", "A switch that starts on Yes or No is already answered, so Required has nothing left to enforce. Leave it on \u201cNo answer yet\u201d if the customer must actively choose.")}</div>
      </div>

      <Row
        on={cfg.readOnly}
        onToggle={() => set({ readOnly: !cfg.readOnly })}
        title={t("questions.textRules.readOnly", "Read-only")}
        sub={t("questions.yesno.readOnlySub", "The customer sees the switch and its value but cannot change it \u2014 for a state set elsewhere, such as consent captured at sign-up.")}
      />

      {cfg.readOnly && cfg.startsAs === "unset" ? (
        <Warn>{t("questions.yesno.readOnlyUnsetWarn", "Read-only with no starting answer leaves the customer stuck: they cannot set it, and it stays unanswered. Pick Yes or No above.")}</Warn>
      ) : null}
      {required && cfg.startsAs !== "unset" ? (
        <Warn>{t("questions.yesno.requiredPrefilledWarn", "This field is marked Required, but it opens already answered \u2014 the rule can never fail. Start it on \u201cNo answer yet\u201d if you want the customer to choose deliberately.")}</Warn>
      ) : null}
    </Card>
  );
}

/* ── File upload ──────────────────────────────────────────────────────────── */
/* ── Info text ────────────────────────────────────────────────────────────── */

/**
 * The banner treatment for a tone. Exported so the config card's swatches and
 * the live preview render from ONE definition — a preview that only approximates
 * the real thing is worse than no preview.
 *
 * Surfaces are derived from the semantic-1 tokens via alpha(): the design system
 * ships no -3/-4 surface step for green/orange/red/blue, and inventing hex
 * values would drift from the palette.
 */
export function infoToneStyle(tone: QInfoToneId): { bg: string; border: string; fg: string; icon: ReactNode } | null {
  switch (tone) {
    case "info":    return { bg: alpha(T.blue1, 10),   border: alpha(T.blue1, 30),   fg: T.blue1,   icon: <Icon.info size={14} /> };
    case "success": return { bg: alpha(T.green1, 10),  border: alpha(T.green1, 30),  fg: T.green1,  icon: <Icon.check size={14} /> };
    case "warning": return { bg: alpha(T.orange1, 10), border: alpha(T.orange1, 30), fg: T.orange1, icon: <Icon.warn size={14} /> };
    case "error":   return { bg: alpha(T.red1, 10),    border: alpha(T.red1, 30),    fg: T.red1,    icon: <Icon.warn size={14} /> };
    default:        return null; // plain — no banner chrome at all
  }
}

export function QuestionInfoRules({ cfg, onChange }: { cfg: QInfoConfig; onChange: (next: QInfoConfig) => void }) {
  const t = useT();
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.info.appearance", "Appearance")}</div>

      <div>
        <div style={lbl}>{t("questions.info.style", "Style")}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {Q_INFO_TONES.map((tone) => {
            const on = cfg.tone === tone.id;
            const s = infoToneStyle(tone.id);
            return (
              /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
              <button
                key={tone.id}
                type="button"
                onClick={() => onChange({ ...cfg, tone: tone.id })}
                data-testid={`qinfo-tone-${tone.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", borderRadius: 9,
                  border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white,
                  cursor: "pointer", fontFamily: T.font, textAlign: "left",
                }}
              >
                {/* A real swatch in the tone's own colours, not a generic dot. */}
                <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: s ? s.bg : T.n6, border: `1px solid ${s ? s.border : T.n4}`, color: s ? s.fg : T.n3 }}>
                  {s ? s.icon : <span style={{ fontSize: 10, fontWeight: 700 }}>{t("questions.configCards.text", "T")}</span>}
                </span>
                <span style={{ fontSize: 13, fontWeight: on ? 600 : 500, color: on ? T.p1 : T.n1 }}>{t(tone.labelKey, tone.label)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {cfg.tone !== "plain" ? (
        <div>
          <div style={lbl}>{t("questions.info.title", "Banner title")}</div>
          <input
            value={cfg.title ?? ""}
            onChange={(e) => onChange({ ...cfg, title: e.target.value })}
            placeholder={t("questions.info.titlePlaceholder", "Optional bold lead-in, e.g. Please note")}
            data-testid="qinfo-title"
            style={inp()}
          />
          <div style={hint}>{t("questions.info.titleHint", "Sits above the text in bold. Leave empty for body text only.")}</div>
        </div>
      ) : (
        <div style={hint}>{t("questions.info.plainHint", "Plain text renders as a paragraph with no background — the default, and what existing info-text questions use.")}</div>
      )}
    </Card>
  );
}

/* ── Dropdown ─────────────────────────────────────────────────────────────── */
export function QuestionSelectRules({ cfg, optionCount, placeholder, onPlaceholder, onChange }: { cfg: QSelectConfig; optionCount: number; placeholder: string; onPlaceholder: (next: string) => void; onChange: (next: QSelectConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QSelectConfig>) => onChange({ ...cfg, ...patch });
  // The default is the control's own resting text, and it changes with the
  // cardinality — so the field shows what the customer would ACTUALLY see if the
  // author writes nothing, rather than a generic "Placeholder" hint.
  const fallback = cfg.allowMultiple
    ? t("questions.editorPage.chooseAny", "Choose any…")
    : t("questions.editorPage.chooseOne", "Choose one…");
  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.select.dropdown", "Dropdown")}</div>

      <div>
        <div style={lbl}>{t("questions.select.placeholder", "Placeholder")}</div>
        <input
          value={placeholder}
          onChange={(e) => onPlaceholder(e.target.value)}
          placeholder={fallback}
          data-testid="qselect-placeholder"
          style={inp()}
        />
        <div style={hint}>{t("questions.select.placeholderHint", "The closed dropdown's text before anything is picked. Leave empty to use the default shown here.")}</div>
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => set({ allowMultiple: !cfg.allowMultiple })}
        title={t("questions.select.allowMultiple", "Allow several selections")}
        sub={t("questions.select.allowMultipleSub", "The customer can pick more than one option. Off, it is a single choice.")}
      />
      <Row
        on={cfg.sortAlphabetically}
        onToggle={() => set({ sortAlphabetically: !cfg.sortAlphabetically })}
        title={t("questions.select.sortAlpha", "Sort options alphabetically")}
        sub={t("questions.select.sortAlphaSub", "Shown A–Z to the customer instead of the order you listed them in. Your own order below is kept.")}
      />
      {cfg.sortAlphabetically && optionCount > 1 ? (
        <div style={hint}>{t("questions.select.sortAlphaNote", "Sorting is per language — a translated list sorts by its own labels, so the order can differ between languages.")}</div>
      ) : null}
    </Card>
  );
}

export function QuestionFileRules({ cfg, onChange }: { cfg: QFileConfig; onChange: (next: QFileConfig) => void }) {
  const t = useT();
  const set = (patch: Partial<QFileConfig>) => onChange({ ...cfg, ...patch });
  const toggleFormat = (id: QFileFormatId) => {
    const has = cfg.formats.includes(id);
    set({ formats: has ? cfg.formats.filter((f) => f !== id) : [...cfg.formats, id] });
  };
  const groups: { id: string; label: string }[] = [
    { id: "images", label: t("questions.file.images", "Images") },
    { id: "documents", label: t("questions.file.documents", "Documents") },
  ];
  const size = fileSize(cfg.maxSize);

  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={cardTitle}>{t("questions.file.upload", "Upload")}</div>

      <div>
        <div style={lbl}>{t("questions.file.acceptedTypes", "Accepted file types")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {groups.map((g) => (
            <div key={g.id}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{g.label}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {Q_FILE_FORMATS.filter((f) => f.group === g.id).map((f) => {
                  const on = cfg.formats.includes(f.id);
                  return (
                    /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleFormat(f.id)}
                      data-testid={`qfile-format-${f.id}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 11px", borderRadius: 8,
                        border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white,
                        color: on ? T.p1 : T.n1, fontWeight: on ? 600 : 500, fontSize: 12.5, fontFamily: T.font, cursor: "pointer",
                      }}
                    >
                      <span style={{ width: 14, height: 14, borderRadius: 4, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: on ? T.p1 : T.white, border: `1px solid ${on ? T.p1 : T.n4}`, color: T.white }}>
                        {on ? <Icon.check size={10} /> : null}
                      </span>
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        {cfg.formats.length === 0 ? (
          <div style={{ marginTop: 10 }}>
            <Warn>{t("questions.file.noFormatsWarn", "No type selected — the field will accept any file. Pick at least one to restrict it.")}</Warn>
          </div>
        ) : null}
      </div>

      <div style={{ height: 1, background: T.border }} />

      <div>
        <div style={lbl}>{t("questions.file.maxSize", "Maximum size")}</div>
        <Dropdown
          options={Q_FILE_SIZES.map((s) => ({ value: s.id, label: t("questions.config.fileSizeOption", "{label} · {mb} MB", { label: t(s.labelKey, s.label), mb: s.mb }) }))}
          value={cfg.maxSize}
          onChange={(v) => set({ maxSize: v as QFileSizeId })}
          aria-label={t("questions.file.maxSize", "Maximum size")}
          testid="qfile-max-size"
          style={{ gap: 0, width: "100%" }}
        />
        <div style={hint}>
          {t("questions.configCards.text2", "{arg1} {arg2} {mb} MB.", { arg1: t("questions.file.maxSizeHint", "Per file, not per booking."), arg2: t("questions.file.maxSizeCurrent", "Currently"), mb: size.mb })}
        </div>
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => set({ allowMultiple: !cfg.allowMultiple })}
        title={t("questions.file.allowMultiple", "Allow several files")}
        sub={t("questions.file.allowMultipleSub", "The customer can attach more than one file. The size limit applies to each file separately.")}
      />
    </Card>
  );
}

/* ── Stepper ──────────────────────────────────────────────────────────────── */
export function QuestionStepperRules({ cfg, onChange }: { cfg: QStepperConfig; onChange: (next: QStepperConfig) => void }) {
  const t = useT();
  const rows = cfg.rows.length ? cfg.rows : [newStepperRow("")];
  const setRow = (id: string, patch: Partial<QStepperRow>) =>
    onChange({ ...cfg, rows: rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const num = (raw: string): number | null => {
    const n = parseInt(raw, 10);
    return raw.trim() === "" || Number.isNaN(n) ? null : n;
  };

  return (
    <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14, marginTop: 6 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={cardTitle}>{t("questions.stepper.counts", "Counts")}</div>
        {cfg.allowMultiple ? (
          <Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => onChange({ ...cfg, rows: [...rows, newStepperRow("")] })}>
            {t("questions.stepper.addCount", "Add count")}
          </Btn>
        ) : null}
      </div>

      <Row
        on={cfg.allowMultiple}
        onToggle={() => onChange({ ...cfg, allowMultiple: !cfg.allowMultiple })}
        title={t("questions.stepper.allowMultiple", "Allow several values")}
        sub={t("questions.stepper.allowMultipleSub", "One question holding a few separate counts — Adults, Children, and so on. Off, it is a single count.")}
      />

      {rows.map((r, i) => {
        const conflict = typeof r.min === "number" && typeof r.max === "number" && r.min > r.max;
        const defOut = typeof r.defaultValue === "number" && ((typeof r.min === "number" && r.defaultValue < r.min) || (typeof r.max === "number" && r.defaultValue > r.max));
        return (
          <div key={r.id} style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, border: `1px solid ${T.border}`, borderRadius: 8, background: T.canvas }}>
            {cfg.allowMultiple ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {t("questions.configCards.text3", "{arg1} {arg2}", { arg1: t("questions.stepper.count", "Count"), arg2: i + 1 })}
                </span>
                {rows.length > 1 ? (
                  <IconBtn size={28} label={t("common.actions.remove", "Remove")} onClick={() => onChange({ ...cfg, rows: rows.filter((x) => x.id !== r.id) })}>
                    <Icon.x size={12} />
                  </IconBtn>
                ) : null}
              </div>
            ) : null}

            <div>
              <div style={lbl}>{t("questions.stepper.label", "Label")}</div>
              <input
                value={r.label}
                onChange={(e) => setRow(r.id, { label: e.target.value })}
                placeholder={t("questions.stepper.labelPlaceholder", "e.g. Adults")}
                data-testid={`qstep-label-${i}`}
                style={inp()}
              />
            </div>

            <div>
              <div style={lbl}>{t("questions.stepper.helper", "Helper text")}</div>
              <input
                value={r.helper}
                onChange={(e) => setRow(r.id, { helper: e.target.value })}
                placeholder={t("questions.stepper.helperPlaceholder", "e.g. How many adults are coming?")}
                data-testid={`qstep-helper-${i}`}
                style={inp()}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div>
                <div style={lbl}>{t("questions.stepper.min", "Lowest")}</div>
                <input type="number" value={r.min ?? ""} onChange={(e) => setRow(r.id, { min: num(e.target.value) })} data-testid={`qstep-min-${i}`} style={inp({ borderColor: conflict ? T.red1 : T.n4 })} />
              </div>
              <div>
                <div style={lbl}>{t("questions.stepper.max", "Highest")}</div>
                <input type="number" value={r.max ?? ""} onChange={(e) => setRow(r.id, { max: num(e.target.value) })} data-testid={`qstep-max-${i}`} style={inp({ borderColor: conflict ? T.red1 : T.n4 })} />
              </div>
              <div>
                <div style={lbl}>{t("questions.stepper.default", "Starts at")}</div>
                <input type="number" value={r.defaultValue ?? ""} onChange={(e) => setRow(r.id, { defaultValue: num(e.target.value) })} data-testid={`qstep-default-${i}`} style={inp({ borderColor: defOut ? T.red1 : T.n4 })} />
              </div>
            </div>

            {conflict ? <Warn>{t("questions.stepper.rangeConflict", "Lowest is above highest — the customer could not pick any value.")}</Warn> : null}
            {defOut && !conflict ? <Warn>{t("questions.stepper.defaultOutOfRange", "The starting value sits outside the allowed range; the field will open clamped to the nearest end.")}</Warn> : null}
          </div>
        );
      })}
    </Card>
  );
}
